import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import client, { API } from "@/api/client";

// Resolves a stored logo URL (which starts with "/api/uploads/..") into an
// absolute URL that works from the frontend origin via the Kubernetes ingress.
function resolveLogoUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  if (url.startsWith("/api/")) return `${backend}${url}`;
  return url;
}

export function Marquee() {
  const { t } = useTranslation();
  const [logos, setLogos] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await client.get("/marquee/logos");
        if (!alive) return;
        setLogos(res.data?.items || []);
      } catch (_) {
        if (alive) setLogos([]);
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (!loaded || logos.length === 0) return null;

  // Duplicate the list enough times so the CSS translateX(-50%) loop is
  // seamless even when only 1-2 logos are configured. Each rendered item is
  // ~180px wide including gaps, so ~10 items give ~1800px which comfortably
  // exceeds most viewport widths; then the whole batch is duplicated once
  // more for the animation loop.
  const MIN_ITEMS = 10;
  const repeatBase = Math.max(1, Math.ceil(MIN_ITEMS / logos.length));
  const base = Array.from({ length: repeatBase }, () => logos).flat();
  const doubled = [...base, ...base];

  return (
    <section
      className="relative border-y border-border/60 bg-muted/20 overflow-hidden"
      data-testid="marquee-section"
      aria-label={t("marquee.title", { defaultValue: "Entities and Government Portals" })}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        <p
          className="text-center text-[11px] uppercase tracking-widest text-muted-foreground mb-5"
          data-testid="marquee-title"
        >
          {t("marquee.title", { defaultValue: "Entities and Government Portals" })}
        </p>

        <div className="relative">
          {/* Edge fade */}
          <div
            className="pointer-events-none absolute inset-y-0 left-0 w-12 sm:w-24 z-10 bg-gradient-to-r from-background to-transparent"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-y-0 right-0 w-12 sm:w-24 z-10 bg-gradient-to-l from-background to-transparent"
            aria-hidden
          />

          <div className="overflow-hidden">
            <div className="marquee-track flex w-max items-center gap-10 sm:gap-14">
              {doubled.map((logo, i) => {
                const src = resolveLogoUrl(logo.url);
                const alt = logo.name || "logo";
                const isFirstOccurrence = i < logos.length;
                const img = (
                  <img
                    src={src}
                    alt={alt}
                    loading="lazy"
                    className="h-10 sm:h-12 w-auto max-w-[160px] object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                );
                return (
                  <div
                    key={`${logo.logo_id}-${i}`}
                    className="shrink-0 flex items-center"
                    data-testid={isFirstOccurrence ? `marquee-logo-${logo.logo_id}` : undefined}
                    title={logo.name || undefined}
                  >
                    {logo.link ? (
                      <a
                        href={logo.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={alt}
                      >
                        {img}
                      </a>
                    ) : (
                      img
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Marquee;
